import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/apiClient";
import { MissingDocsAlert, missingDocs } from "./MissingDocs";
import { PrintOptionsModal } from "./PrintOptionsModal";
import { ageOf, fmtBirth } from "./IdOcrCompare";

const isBirthday = (s) => { if (!s) return false; const n = new Date(); return s.slice(5) === `${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`; };
import { ArrowLeft, Cake, ExternalLink, FilePlus2, FileSignature, FolderArchive, ListOrdered, Mail, MapPin, MessageCircle, Pencil, Phone, Printer, Star, Trash2 } from "lucide-react";
import { money, calcCommission, effectiveRate, calcAgentCommission } from "../lib/finance";
import { waTarget } from "../lib/wa";
import { CustomerTimeline } from "./CustomerTimeline";
import { DocumentArchive } from "./DocumentArchive";
import { VipBadge } from "./Customers";

export const fullAddress = (c) => [c.address, [c.district, c.city].filter(Boolean).join(" / ")].filter(Boolean).join(" · ");

function Header({ c, actions }) {
  const wa = waTarget(c.phone);
  const addr = fullAddress(c);
  return (
    <div className="ccard-head">
      <div className="avatar large">{c.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}</div>
      <div className="ccard-id">
        <h1 data-testid="customer-card-name">{c.name}{actions.vip}
          {actions.onToggleFavorite && <button type="button" className="ccard-star" onClick={() => actions.onToggleFavorite(c)} data-testid="customer-favorite-toggle" title="Favori"><Star size={18} fill={c.favorite ? "#f59e0b" : "none"} color={c.favorite ? "#f59e0b" : "#94a3b8"} /></button>}
        </h1>
        <p>{c.type}{c.taxNo ? ` · ${c.taxNo}` : ""}{c.birthDate && <span data-testid="customer-card-birth"> · <Cake size={12} /> {fmtBirth(c.birthDate)} ({ageOf(c.birthDate)} yaş)</span>}</p>
        {isBirthday(c.birthDate) && <span className="birthday-badge" data-testid="customer-card-birthday"><Cake size={13} /> Bugün doğum günü!</span>}
        <div className="customer-contact-row">
          {c.phone && <a className="contact-chip" href={`tel:${c.phone}`} data-testid="ccard-call"><Phone size={11} /> {c.phone}</a>}
          {wa && <a className="contact-chip whatsapp" href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="ccard-wa"><MessageCircle size={11} /> WhatsApp</a>}
          {c.email && <a className="contact-chip" href={`mailto:${c.email}`} data-testid="ccard-mail"><Mail size={11} /> {c.email}</a>}
        </div>
        {addr && <div className="ccard-address" data-testid="customer-card-address"><MapPin size={13} /> {addr}</div>}
      </div>
    </div>
  );
}

export const customerInfoRows = (c) => [
  ["İl / İlçe", [c.city, c.district].filter(Boolean).join(" / ")],
  ["Açık adres", c.address],
  ["E-posta", c.email],
  ["T.C. / Vergi no", c.taxNo],
  ["Doğum tarihi", c.birthDate ? `${fmtBirth(c.birthDate)} (${ageOf(c.birthDate)} yaş)` : ""],
  ["Komisyon oranı", Number(c.commissionRate) > 0 ? `%${c.commissionRate}${c.commissionPerson ? ` · ${c.commissionPerson}` : ""}` : ""],
];

export function CustomerInfoGrid({ c }) {
  return (
    <dl className="ccard-info" data-testid="customer-card-info">
      {customerInfoRows(c).map(([l, v]) => (
        <div key={l}><dt>{l}</dt><dd data-testid={`ccard-info-${l}`}>{v || <em className="dim">—</em>}</dd></div>
      ))}
    </dl>
  );
}

export function CustomerCard({ onQuotesSent, customer: c, policies, quotes, vehicles = [], onVehicleSaved, onCustomerUpdated, branches, settings, onBack, onEdit, onNewPolicy, onNewQuote, onOpenPolicy, onOpenQuote, onToggleFavorite, onOpenPortal, onDelete }) {
  const [tab, setTab] = useState("records");
  const [docs, setDocs] = useState(null);
  const [preset, setPreset] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  useEffect(() => { setDocs(null); axios.get(`${API}/customers/${c.id}/documents`).then(({ data }) => setDocs(data)).catch(() => setDocs([])); }, [c.id]);
  const missing = missingDocs(c, policies, vehicles, docs);
  const pols = policies.filter((p) => p.customer === c.name);
  const qts = quotes.filter((q) => q.customerId === c.id || (!q.customerId && q.customerName === c.name));
  const prim = pols.reduce((s, p) => s + Number(p.premium || 0), 0);
  const personal = pols.reduce((s, p) => s + calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal, 0);
  const agent = pols.reduce((s, p) => s + calcAgentCommission(p, [c], branches, settings).agent, 0);
  const vip = <VipBadge customer={c} policies={policies} branches={branches} settings={settings} />;
  return (
    <div className="ccard" data-testid={`customer-card-page-${c.id}`}>
      <div className="ccard-top">
        <button type="button" className="text-button" onClick={onBack} data-testid="customer-card-back"><ArrowLeft size={15} /> Müşteriler</button>
        <div className="ccard-tools">
          <button className="ghost-button" onClick={() => setPrintOpen(true)} data-testid="customer-card-print"><Printer size={14} /> Yazdır</button>
          {onOpenPortal && <button className="ghost-button" onClick={() => onOpenPortal(c)} data-testid="customer-portal-button"><ExternalLink size={14} /> Portal linki</button>}
          <button className="ghost-button" onClick={onEdit} data-testid="customer-card-edit"><Pencil size={14} /> Düzenle</button>
          {onDelete && <button className="ghost-button danger" onClick={onDelete} data-testid="customer-card-delete"><Trash2 size={14} /> Sil</button>}
        </div>
      </div>
      <section className="panel ccard-panel">
        <div className="ccard-hero">
          <div className="ccard-hero-left">
            <Header c={c} actions={{ vip, onToggleFavorite }} />
            <div className="ccard-cta">
              <button className="primary-button" onClick={onNewQuote} data-testid="customer-card-new-quote"><FileSignature size={15} /> Yeni teklif</button>
              <button className="primary-button dark" onClick={onNewPolicy} data-testid="customer-card-new-policy"><FilePlus2 size={15} /> Yeni poliçe</button>
            </div>
          </div>
          <CustomerInfoGrid c={c} />
        </div>
        <div className="detail-stats ccard-stats">
          <div><span>Poliçe</span><strong data-testid="ccard-stat-policies">{pols.length}</strong></div>
          <div><span>Teklif</span><strong data-testid="ccard-stat-quotes">{qts.length}</strong></div>
          <div><span>Toplam brüt prim</span><strong>{money(prim)}</strong></div>
          <div><span>Kişisel hak ediş</span><strong>{money(personal)}</strong></div>
          <div><span>Acente hak edişi{Number(c.commissionRate) > 0 ? ` (%${c.commissionRate})` : ""}</span><strong data-testid="ccard-stat-agent">{Number(c.commissionRate) > 0 ? money(agent) : "—"}</strong></div>
        </div>
        <MissingDocsAlert items={missing} onFix={(m) => { setTab("docs"); setPreset({ ...m, at: Date.now() }); }} />
        {c.note && <p className="ccard-note">{c.note}</p>}
      </section>
      <div className="ccard-tabs" role="tablist">
        <button type="button" className={tab === "records" ? "active" : ""} onClick={() => setTab("records")} data-testid="customer-tab-records"><ListOrdered size={15} /> Teklif & Poliçeler <b>{pols.length + qts.length}</b></button>
        <button type="button" className={tab === "docs" ? "active" : ""} onClick={() => setTab("docs")} data-testid="customer-tab-docs"><FolderArchive size={15} /> Belge arşivi {docs && <b>{docs.length}</b>}{missing.length > 0 && <i className="tab-alert" data-testid="docs-tab-alert">{missing.length}</i>}</button>
      </div>
      <section className="panel">
        {tab === "records"
          ? <CustomerTimeline onQuotesSent={onQuotesSent} onDocCreated={(d) => d && setDocs((list) => [d, ...(list || [])])} policies={pols} quotes={qts} customer={c} branches={branches} settings={settings} onOpenPolicy={onOpenPolicy} onOpenQuote={onOpenQuote} />
          : <DocumentArchive customer={c} docs={docs} setDocs={setDocs} preset={preset} vehicles={vehicles} onVehicleSaved={onVehicleSaved} onCustomerUpdated={onCustomerUpdated} />}
      </section>
      {printOpen && <PrintOptionsModal customer={c} policies={policies} branches={branches} settings={settings} onClose={() => setPrintOpen(false)} onDocCreated={(d) => setDocs((list) => [d, ...(list || [])])} />}
    </div>
  );
}
